/**
 * What every editor layout shares, whatever frames its panels: global
 * keyboard shortcuts and clipboard events, the Preferences dialog, the tour
 * (its overlay and offers), and the menu items features contribute. The
 * desktop layout (EditorShell) and the compact one (CompactShell) differ only
 * in the frame they draw around the same panels.
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { runEdit, useEditor } from "../EditorContext";
import { duplicateEdit } from "../create/entityCopies";
import { onClipboardEvent } from "../clipboard/clipboardEvents";
import { copyLevelJsonItem } from "../levels/levelJson";
import { useTour } from "../hooks/editorHooks";
import type { MenuItem } from "./MenuBar";
import { PreferencesDialog } from "../preferences/PreferencesDialog";
import { AboutDialog } from "../about/AboutDialog";
import { TourOffer } from "../tour/TourOffer";
import { TourOverlay } from "../tour/TourOverlay";
import { TourTargetsProvider } from "../tour/TourTargets";
import type { PlayState } from "../play/PlaySession";
import type { EditorLayout } from "./layout";
import { EditorLayoutContext } from "./EditorLayoutContext";
import { deleteEdit, visibilityOf } from "../selection/selectionEdits";
import { focusKind, shortcutFor } from "./shortcuts";

/** Items features add to the menus, by menu title. */
export type MenuItems = Readonly<Record<string, readonly MenuItem[]>>;

export interface TourOfferProp {
  /** Show the first-run tour offer (A-25); the app decides from storage and the arrival URL. */
  readonly tourOffer?: { readonly onClose: (dontShowAgain: boolean) => void } | null;
}

export interface ShellFrameProps extends TourOfferProp {
  readonly className: string;
  /** The layout the frame holds, which the tour offer's diagram draws. */
  readonly layout: EditorLayout;
  /** The layout, given the menu items every layout offers. */
  readonly children: (menuItems: MenuItems) => ReactNode;
}

export function ShellFrame({ tourOffer = null, className, layout, children }: ShellFrameProps) {
  const { dispatch } = useEditor();
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(tourOffer !== null);
  const tour = useTour();
  const menuItems = useSharedMenuItems({
    openPreferences: () => {
      setPreferencesOpen(true);
    },
    openAbout: () => {
      setAboutOpen(true);
    },
  });
  useEditorShortcuts();
  useClipboardEvents();

  return (
    <TourTargetsProvider>
      <EditorLayoutContext.Provider value={layout}>
        <div className={className}>
          {children(menuItems)}
          {preferencesOpen && (
            <PreferencesDialog
              onClose={() => {
                setPreferencesOpen(false);
              }}
            />
          )}
          {aboutOpen && (
            <AboutDialog
              onClose={() => {
                setAboutOpen(false);
              }}
            />
          )}
          <TourOverlay />
          {offerOpen && tourOffer && (
            <TourOffer
              variant="firstRun"
              layout={layout}
              onStart={() => {
                setOfferOpen(false);
                void dispatch({ type: "tour.start", payload: {}, origin: "tour" });
              }}
              onClose={(dontShowAgain) => {
                setOfferOpen(false);
                tourOffer.onClose(dontShowAgain);
              }}
            />
          )}
          {tour.offered && !offerOpen && (
            <TourOffer
              variant="replay"
              layout={layout}
              onStart={() => void dispatch({ type: "tour.start", payload: {}, origin: "tour" })}
              onClose={() => void dispatch({ type: "tour.end", payload: {}, origin: "tour" })}
            />
          )}
        </div>
      </EditorLayoutContext.Provider>
    </TourTargetsProvider>
  );
}

/** The dialogs the shared menu items open. */
interface ShellDialogs {
  readonly openPreferences: () => void;
  readonly openAbout: () => void;
}

/**
 * Menu items every layout has: Edit › Preferences…, Window › Statistics,
 * Help › About kn.engine (last, as editors put it), and dev-only Level items.
 */
function useSharedMenuItems({ openPreferences, openAbout }: ShellDialogs): MenuItems {
  const { dispatch, world, components, log } = useEditor();

  return {
    Edit: [{ label: "Preferences…", run: openPreferences }],
    Help: [{ label: "About kn.engine", run: openAbout }],
    Window: [
      {
        label: "Statistics",
        run: () =>
          void dispatch({ type: "panel.focus", payload: { panel: "statistics" }, origin: "menu" }),
      },
    ],
    // A developer's read-out for arranging levels in the editor; `pnpm dev` only,
    // so the production bundle doesn't carry it.
    Level: import.meta.env.DEV
      ? [
          copyLevelJsonItem({
            world,
            components,
            log,
            clipboard: navigator.clipboard,
            print: (text) => {
              console.log(text);
            },
          }),
        ]
      : [],
  };
}

/** Routes copy, cut and paste on the editor (Ctrl+C, Ctrl+X, Ctrl+V) to the entity edits. */
function useClipboardEvents(): void {
  const { clipboard, components, dispatch, dispatchAll, selection, world } = useEditor();

  useEffect(() => {
    const onEvent = (event: ClipboardEvent) => {
      onClipboardEvent(event, focusKind(document.activeElement), {
        world: world.getSnapshot(),
        registry: components,
        selection: selection.getSnapshot(),
        run: (edit) => {
          runEdit({ dispatch, dispatchAll }, edit);
        },
        hold: (text) => {
          clipboard.hold(text);
        },
        kept: () => clipboard.kept(),
      });
    };
    const types = ["copy", "cut", "paste"] as const;
    for (const type of types) document.addEventListener(type, onEvent);
    return () => {
      for (const type of types) document.removeEventListener(type, onEvent);
    };
  }, [clipboard, components, dispatch, dispatchAll, selection, world]);
}

/** Routes global key presses through `shortcutFor` to the Command Bus. */
function useEditorShortcuts(): void {
  const { components, dispatch, dispatchAll, selection, settings, world, play } = useEditor();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;
      const flying = document.querySelector('.kn-viewport-panel[data-flying="true"]') !== null;
      const focus = focusKind(document.activeElement);
      const { ids, active } = selection.getSnapshot();
      const snapshot = world.getSnapshot();
      const edit = shortcutFor(event, {
        focus,
        flying,
        active: visibilityOf(world, active === null ? [] : [active])[0] ?? null,
        selected: visibilityOf(world, ids),
        deletion: deleteEdit(snapshot.entities, selection.getSnapshot(), ids, "keyboard"),
        duplication: duplicateEdit(snapshot, components, ids, "keyboard"),
        gameView: settings.getSnapshot().gameView,
        playing: playingKeys(play.getSnapshot()),
      });
      if (!edit) return;

      event.preventDefault();
      runEdit({ dispatch, dispatchAll }, edit);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [components, dispatch, dispatchAll, selection, settings, world, play]);
}

/** What play's keys need: how many zones there are, and which one is showing. */
function playingKeys(state: PlayState | null) {
  return state && { stops: state.stops.length, current: state.current };
}
